import React, { useState, useEffect } from 'react'
import NetworkingBriefProvider, {
  useNetworkingBrief,
} from './NetworkingBriefProvider'
import { TemplateProvider, initializeTemplates } from '../../lib/pdf'
import { Textarea } from '../../lib/ui/textarea'
import { Button } from '../../lib/ui/button'
import { Toaster } from '../../lib/ui/toast'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
} from '~/components/shared/TabNav'
import {
  IdentificationIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import Editor from './Editor/index'
import PdfViewerPanel from './PdfViewerPanel'
import StyleEditor from './StyleEditor'
import * as T0 from './Template0'
import useTranslation, {
  TranslationProvider,
} from '../shared/TranslationProvider'
import translations from './locales.json'

// These need to be declared outside of a component so they can't affect rendering
const [templates, templateStyle, templateSupportedStyles] = initializeTemplates(
  [T0],
)

const NetworkingBriefBuilder = ({
  template_settings,
  template_index,
  locale,
  ...props
}) => {
  const defaultStyle = template_settings
    ? {
        ...templateStyle,
        [`template-${template_index}`]: template_settings,
      }
    : templateStyle
  const templateProps = {
    templates,
    defaultStyle,
    templateSupportedStyles,
    template_index,
    dev_mode: props.dev_mode,
  }

  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope={'javascript.components.NetworkingBriefBuilder'}
    >
      <TemplateProvider {...templateProps}>
        <NetworkingBriefProvider {...props}>
          <NetworkingBriefBuilderInner />
        </NetworkingBriefProvider>
      </TemplateProvider>
    </TranslationProvider>
  )
}
export default NetworkingBriefBuilder

const NetworkingBriefBuilderInner = () => {
  const { devMode } = useNetworkingBrief()
  const { t } = useTranslation()

  const defaultActiveTab = 'details'

  const instructions = Array.isArray(t('brief_builder_instructions_html'))
    ? t('brief_builder_instructions_html')
    : [t('brief_builder_instructions_html')]

  return (
    <>
      <div className="p-4 md:p-12">
        <h1>{t('networking_brief_title')}</h1>
        <p className="w-4/5">{t('brief_builder_intro')}</p>
        <ul className="list-inside list-disc">
          {instructions.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>
      <div className="flex w-full flex-col md:flex-row">
        <div className="md:basis-3/5">
          <TabNav
            className="px-4 pt-4 lg:px-8 xl:px-12"
            defaultActiveTab={defaultActiveTab}
            mobileMode="swipe"
          >
            <ButtonContainer className="md:justify-start">
              <TabButton
                name="details"
                text={t('details')}
                Icon={IdentificationIcon}
              />
              <TabButton
                name="formattingAndDesign"
                text={t('formatting_and_design')}
                Icon={ClipboardDocumentCheckIcon}
              />
              {devMode && (
                <TabButton
                  name="text"
                  text={t('raw_json')}
                  Icon={DocumentTextIcon}
                />
              )}
            </ButtonContainer>
            <PanelContainer className="md:min-h-[130vh]">
              <TabPanel name="details" className="pt-8">
                <Editor />
              </TabPanel>
              <TabPanel name="formattingAndDesign" className="pt-8">
                <StyleEditor />
              </TabPanel>
              <TabPanel name="text" className="pt-8">
                <TextEditor />
              </TabPanel>
            </PanelContainer>
          </TabNav>
        </div>
        <div id="resume-pdf-preview" className="relative md:flex-grow">
          <div className="h-screen w-full md:sticky md:left-0 md:top-0">
            <PdfViewerPanel />
          </div>
        </div>
        <Toaster />
      </div>
    </>
  )
}

const TextEditor = () => {
  const { networkingBriefDocument, setNetworkingBriefDocument } =
    useNetworkingBrief()
  const [editorNetworkingBrief, setEditorNetworkingBrief] = useState(
    JSON.stringify(networkingBriefDocument, null, 2),
  )

  const sync = () => {
    const updatedNetworkingBrief = JSON.parse(editorNetworkingBrief)
    setNetworkingBriefDocument(updatedNetworkingBrief)
  }

  useEffect(() => {
    setEditorNetworkingBrief(JSON.stringify(networkingBriefDocument, null, 2))
  }, [networkingBriefDocument])

  const { t } = useTranslation()

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-row justify-evenly">
        <Button onClick={sync}>{t('sync')}</Button>
      </div>
      <Textarea
        className="h-full w-full"
        value={editorNetworkingBrief}
        onChange={(e) => setEditorNetworkingBrief(e.target.value)}
      ></Textarea>
    </div>
  )
}
